import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';

const ToastContext = createContext(null);

let toastIdCounter = 0;

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

const ICONS = {
  success: { name: 'CheckCircle', color: 'text-green-500' },
  error: { name: 'XCircle', color: 'text-red-500' },
  warning: { name: 'AlertTriangle', color: 'text-amber-500' },
  info: { name: 'Info', color: 'text-muted-foreground' },
};

const Toast = ({ id, type = 'info', message, onDismiss }) => {
  const icon = ICONS[type] || ICONS.info;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 80, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 80, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      className="pointer-events-auto flex items-start gap-3 w-80 bg-card border border-border rounded-xl shadow-lg px-4 py-3"
    >
      <Icon name={icon.name} size={20} className={`flex-shrink-0 mt-0.5 ${icon.color}`} />
      <p className="text-sm text-foreground flex-1 leading-snug">{message}</p>
      <button
        onClick={() => onDismiss(id)}
        className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      >
        <Icon name="XCircle" size={14} />
      </button>
    </motion.div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev.slice(-4), { id, message, type }]); // max 5
    if (duration > 0) {
      setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  const api = {
    toast,
    success: (msg, duration) => toast(msg, 'success', duration),
    error: (msg, duration) => toast(msg, 'error', duration ?? 6000),
    warning: (msg, duration) => toast(msg, 'warning', duration ?? 5000),
    info: (msg, duration) => toast(msg, 'info', duration),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Toast container — bottom-right */}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        <AnimatePresence mode="popLayout">
          {toasts.map((t) => (
            <Toast key={t.id} {...t} onDismiss={dismiss} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};

export default Toast;
